export interface ProgramUser {
	id: string;
	email: string;
	full_name: string;
	groups?: string[];
}

export interface ProgramOrderLineItem {
	id: string;
	name: string;
	order_name: string | null;
	thumbnail_url: string | null;
	quantity: number;
	unit: string;
	length: number;
	width: number;
	height: number;
	material_name: string | null;
	finish_name: string | null;
	required_part_ship_date: string | null;
	shop_due_date?: string | null;
}

/**
 * Which programming discipline a program records work for. Reviews, issues and
 * feedback carry no type of their own — they inherit one from their program.
 */
export type ProgramType = 'cam' | 'keyence' | 'inspection_plan';

/**
 * `negligent` sits above `critical` and is priced at double it — reserved for
 * work that went wrong because something known was skipped.
 */
export type ProgramIssueSeverity =
	| 'low'
	| 'medium'
	| 'high'
	| 'critical'
	| 'negligent';

export type ProgramIssueCaughtPhase =
	| 'review'
	| 'secondary_review'
	| 'production'
	| 'qc'
	| 'delivery'
	| 'shipping'
	| 'customer';

export const PROGRAM_TYPE_LABELS: Record<ProgramType, string> = {
	cam: 'CAM Programming',
	keyence: 'Keyence Programming',
	inspection_plan: 'Inspection Plan Programming',
};

/** CAM and Keyence support multiple programs per line item with distinct filenames. */
export function programTypeUsesFilename(programType: ProgramType): boolean {
	return programType === 'cam' || programType === 'keyence';
}

export function programTypeAllowsMultiplePrograms(
	programType: ProgramType
): boolean {
	return programType === 'cam' || programType === 'keyence';
}

export interface ProgramBasedOnSummary {
	id: string;
	filename: string;
}

/** Expected Keyence program filename from the line item's inspection plan. */
export interface KeyenceFilenameSuggestion {
	filename: string;
	view: string;
	device_name: string | null;
	already_logged: boolean;
}

/**
 * Another copy of the same part on the same order, which may therefore share
 * one program. `already_linked` is relative to the program being edited.
 */
export type LinkableLineItem = ProgramOrderLineItem & {
	already_linked: boolean;
};

export interface LinkableLineItemsResponse {
	results: LinkableLineItem[];
}

export interface SetProgramLineItemsPayload {
	program: string;
	order_line_items: string[];
	/** `set` replaces the group; `add` extends it. Defaults to `set`. */
	mode?: 'set' | 'add';
}

export const PROGRAM_ISSUE_SEVERITY_LABELS: Record<ProgramIssueSeverity, string> =
	{
		low: 'Low',
		medium: 'Medium',
		high: 'High',
		critical: 'Critical',
		negligent: 'Negligent',
	};

export const PROGRAM_ISSUE_CAUGHT_PHASE_LABELS: Record<
	ProgramIssueCaughtPhase,
	string
> = {
	review: 'Review',
	secondary_review: 'Secondary Review',
	production: 'Production',
	qc: 'QC',
	delivery: 'Delivery',
	shipping: 'Shipping',
	customer: 'Customer',
};

/**
 * Whose work introduced the issue. Defaults to the programmer; pointed at a
 * reviewer when their own edit caused it, which takes the charge off the
 * programmer entirely.
 */
export type ProgramIssueFault =
	| 'programmer'
	| 'first_reviewer'
	| 'secondary_reviewer';

export const PROGRAM_ISSUE_FAULT_LABELS: Record<ProgramIssueFault, string> = {
	programmer: 'Programmer',
	first_reviewer: 'First Reviewer',
	secondary_reviewer: 'Secondary Reviewer',
};

export interface ProgramIssue {
	id: string;
	program: string;
	/** Null when the issue was reported outside of any review. */
	review: string | null;
	severity: ProgramIssueSeverity;
	severity_display: string;
	caught_phase: ProgramIssueCaughtPhase;
	caught_phase_display: string;
	fault: ProgramIssueFault;
	fault_display: string;
	/** A training gap rather than a lapse — the person at fault is not charged. */
	insufficient_training: boolean;
	/** Rich text authored in the editor. Image URLs are refreshed server-side. */
	description: string | null;
	reported_by: ProgramUser | null;
	created_time: string;
	modified_time: string;
}

/**
 * Body accepted by the issue write endpoints. Unlike `ProgramIssue` the
 * relations are plain ids: `reported_by` reads back as a nested user but is
 * written as one.
 */
export interface ProgramIssueWritePayload {
	review?: string | null;
	severity?: ProgramIssueSeverity;
	caught_phase?: ProgramIssueCaughtPhase;
	fault?: ProgramIssueFault;
	insufficient_training?: boolean;
	description?: string;
	reported_by?: string | null;
}

/**
 * General-purpose note on a program. Unlike an issue it asserts nothing is
 * wrong — it records observations and context outside any single review.
 */
export interface ProgramFeedback {
	id: string;
	program: string;
	/** Rich text authored in the editor. Image URLs are refreshed server-side. */
	body: string | null;
	author: ProgramUser | null;
	created_time: string;
	modified_time: string;
}

export interface ProgramReview {
	id: string;
	program: string;
	reviewer: ProgramUser | null;
	/**
	 * When the reviewer started the clock. Null on reviews logged after the
	 * fact, whose start was never observed.
	 */
	review_started_at: string | null;
	review_completed_at: string | null;
	review_duration_minutes: number | null;
	/** Rich text authored in the editor. Image URLs are refreshed server-side. */
	feedback: string | null;
	issues: ProgramIssue[];
	issues_count: number;
	created_time: string;
	modified_time: string;
}

export interface Program {
	id: string;
	program_type: ProgramType;
	program_type_display: string;
	/** Exact program filename, e.g. 6198M1004 or 6198M1004 - OPTION 2. */
	filename: string;
	/** CAM only — tracking reference to another program on the same part. */
	based_on_program?: ProgramBasedOnSummary | null;
	/**
	 * The line item that stands in for the group. Every linked line item shares
	 * an order, part name, material and finish, so this reads the same whichever
	 * one the server picks — use `order_line_items` when the count matters.
	 */
	order_line_item: ProgramOrderLineItem;
	/** Every line item this program covers, oldest link first. */
	order_line_items: ProgramOrderLineItem[];
	/** Greater than one when the same part was ordered more than once. */
	linked_line_item_count: number;
	programmer: ProgramUser | null;
	/**
	 * When the programmer started the clock. Null on programs logged after the
	 * fact, whose start was never observed.
	 */
	programming_started_at?: string | null;
	programming_completed_at: string | null;
	programming_duration_minutes: number | null;
	/** Rich text notes from the programmer when stats were logged. */
	programmer_feedback?: string | null;
	/** Only present on the detail endpoint, not in list results. */
	issues?: ProgramIssue[];
	issues_count: number;
	/** Only present on the detail endpoint, not in list results. */
	feedback_entries?: ProgramFeedback[];
	feedback_count: number;
	/** Only present on the detail endpoint, not in list results. */
	reviews?: ProgramReview[];
	reviews_count: number;
	last_review_completed_at: string | null;
	total_review_minutes: number | null;
	latest_reviewer: ProgramUser | null;
	first_reviewer: ProgramUser | null;
	secondary_reviewer: ProgramUser | null;
	first_review_id: string | null;
	secondary_review_id: string | null;
	first_review_duration_minutes: number | null;
	first_review_completed_at: string | null;
	secondary_review_duration_minutes: number | null;
	secondary_review_completed_at: string | null;
	created_time: string;
	modified_time: string;
}

/** Scoring grid row — may represent an OLI without a program yet (`id` is null). */
export interface ProgramScoringRow extends Omit<
	Program,
	'id' | 'created_time' | 'modified_time'
> {
	id: string | null;
	created_time: string | null;
	modified_time: string | null;
}

/**
 * Body accepted by the create endpoint. Unlike `Program` the relations are
 * plain ids. The programmer is omitted for most users so the server attributes
 * the program to whoever is logging it; shop managers may supply one.
 */
export interface ProgramCreatePayload {
	program_type: ProgramType;
	order_line_item: string;
	/**
	 * Additional line items the user approved applying this program to. Only
	 * matching copies of the same part on the same order are accepted.
	 */
	order_line_items?: string[];
	filename?: string;
	based_on_program?: string | null;
	programming_started_at?: string | null;
	programming_completed_at?: string | null;
	programming_duration_minutes?: number | null;
	programmer?: string | null;
	programmer_feedback?: string | null;
}

/**
 * Fields the program write endpoints accept. Distinct from `Program` because
 * the relations go out as plain ids and come back nested.
 */
export type ProgramWritePayload = Partial<
	Omit<ProgramCreatePayload, 'program_type'>
>;

export interface PaginatedProgramResponse {
	count: number;
	next: string | null;
	previous: string | null;
	/** Rows per page, fixed by the server. Drives the pagination controls. */
	page_size: number;
	results: Program[];
}

export interface PaginatedProgramScoringGridResponse {
	count: number;
	next: string | null;
	previous: string | null;
	/** Rows per page, fixed by the server. Drives the pagination controls. */
	page_size: number;
	results: ProgramScoringRow[];
}

export interface ProgramScoringGridFilterUsers {
	programmers: ProgramUser[];
	first_reviewers: ProgramUser[];
	secondary_reviewers: ProgramUser[];
}

export interface EnsureProgramPayload {
	order_line_item: string;
	program_type: ProgramType;
}

export interface PaginatedProgramIssueResponse {
	count: number;
	next: string | null;
	previous: string | null;
	/** Rows per page, fixed by the server. Drives the pagination controls. */
	page_size: number;
	results: ProgramIssue[];
}

export interface PaginatedProgramReviewResponse {
	count: number;
	next: string | null;
	previous: string | null;
	/** Rows per page, fixed by the server. Drives the pagination controls. */
	page_size: number;
	results: ProgramReview[];
}

export interface PaginatedProgramFeedbackResponse {
	count: number;
	next: string | null;
	previous: string | null;
	/** Rows per page, fixed by the server. Drives the pagination controls. */
	page_size: number;
	results: ProgramFeedback[];
}

/** Response from the program image upload endpoint. */
export interface ProgramUploadedImage {
	id: string;
	url: string;
}

/* -------------------------------------------------------------------------- *
 * Scoring
 * -------------------------------------------------------------------------- */

/** The three roles a person can be answerable for on a program. */
export type ProgramScoreRole =
	| 'programmer'
	| 'first_reviewer'
	| 'secondary_reviewer';

export const PROGRAM_SCORE_ROLE_LABELS: Record<ProgramScoreRole, string> = {
	programmer: 'Programming',
	first_reviewer: 'First review',
	secondary_reviewer: 'Secondary review',
};

/** Rolling window the scores are computed over. */
export type ProgramScoreWindow = '30d' | '90d' | '6m' | '12m' | 'all';

export const PROGRAM_SCORE_WINDOW_LABELS: Record<ProgramScoreWindow, string> = {
	'30d': 'Last 30 days',
	'90d': 'Last 90 days',
	'6m': 'Last 6 months',
	'12m': 'Last 12 months',
	all: 'All time',
};

export type ProgramScoreBand =
	| 'on_target'
	| 'watch'
	| 'needs_work'
	| 'critical';

export const PROGRAM_SCORE_BAND_LABELS: Record<ProgramScoreBand, string> = {
	on_target: 'On target',
	watch: 'Watch',
	needs_work: 'Needs work',
	critical: 'Critical',
};

/** The tunable weights the score was computed with, echoed back for the legend. */
export interface ProgramScoreConfig {
	target_score: number;
	allowance_per_program: number;
	/** Share of an issue's points credited back to whoever caught it. */
	catch_credit_fraction: number;
	provisional_program_threshold: number;
	/** Whether a training-flagged issue also clears the reviewers who missed it. */
	training_excuses_reviewers: boolean;
	/** Score points removed per one demerit point per opportunity. */
	score_points_per_demerit: number;
	demerits_scoring_zero: number;
	severity_weights: Record<ProgramIssueSeverity, number>;
}

export interface ProgramScoreRoleBreakdown {
	role: ProgramScoreRole;
	label: string;
	/** Programs the person held this role on inside the window. */
	opportunities: number;
	demerits: number;
	/** Credit earned catching issues in this role, netted off before scoring. */
	credits: number;
	attributed_issues: number;
	escaped_issues: number;
	credited_catches: number;
	/** Issues that would have been charged here but for the training flag. */
	training_excused_issues: number;
	/** Null when the person never held this role in the window. */
	score: number | null;
	/** Demerits per opportunity before credits are netted off. */
	raw_demerits_per_opportunity: number;
	/** Demerits per opportunity after credits, floored at zero. */
	adjusted_demerits_per_opportunity: number;
}

export interface ProgramScoreWeek {
	iso_week: string;
	opportunities: number;
	issues: number;
	escaped_issues: number;
	demerits: number;
	credits: number;
	raw_demerits_per_opportunity: number;
	score: number;
}

export interface ProgramScoreFleetWeek {
	iso_week: string;
	week_start: string;
	programs: number;
	issues: number;
	escaped_issues: number;
	demerits: number;
	raw_demerits_per_program: number;
	score: number;
}

export interface ProgramScoreRow {
	user_id: string;
	full_name: string;
	email: string | null;
	score: number;
	band: ProgramScoreBand;
	/** Sum of opportunities across every role held in the window. */
	opportunities: number;
	demerits: number;
	/** Credit earned across every role, netted against the whole record. */
	credits: number;
	credited_catches: number;
	raw_demerits_per_opportunity: number;
	adjusted_demerits_per_opportunity: number;
	/** True below the configured opportunity threshold — score is not yet evidence. */
	is_provisional: boolean;
	/** Issues excused as training gaps, across every role held. */
	training_excused_issues: number;
	issues_reported: number;
	issues_caught_in_review: number;
	roles: Record<ProgramScoreRole, ProgramScoreRoleBreakdown>;
	weekly: ProgramScoreWeek[];
	rank: number;
}

export interface ProgramScoreTotals {
	programs: number;
	people: number;
	demerits: number;
	raw_demerits_per_program: number;
	adjusted_demerits_per_program: number;
	score: number;
	on_target: number;
	provisional: number;
	training_excused_issues: number;
}

export interface ProgramScoresResponse {
	program_type: ProgramType;
	/** Echoes the resolved window — `custom` when an explicit date range was sent. */
	window: ProgramScoreWindow | 'custom';
	date_from: string | null;
	date_to: string | null;
	config: ProgramScoreConfig;
	totals: ProgramScoreTotals;
	weeks: ProgramScoreFleetWeek[];
	results: ProgramScoreRow[];
}

export interface ProgramMetricsTypeStats {
	programs: number;
	timed_programs: number;
	total_minutes: number;
	avg_minutes: number | null;
}

export interface ProgramMetricsRow {
	user_id: string;
	full_name: string;
	email: string | null;
	programs: Record<ProgramType, number>;
	avg_programming_minutes: Record<ProgramType, number | null>;
	overall_avg_programming_minutes: number | null;
	first_reviews: number;
	secondary_reviews: number;
	reviews: number;
	issues_reported: number;
	issues_attributed: number;
	feedback_entries: number;
	total_programs: number;
}

export interface ProgramMetricsResponse {
	window: ProgramScoreWindow | 'custom';
	date_from: string | null;
	date_to: string | null;
	shop: Record<ProgramType, ProgramMetricsTypeStats>;
	shop_overall: ProgramMetricsTypeStats;
	results: ProgramMetricsRow[];
}

/** One active line item on the CAM Optimizations tab. */
export interface CamOptimizationRow {
	id: string;
	name: string | null;
	order_name: string | null;
	status: string;
	quantity: number;
	required_part_ship_date: string | null;
	thumbnail_url: string | null;
	/** Null when the line item has no usable Fusion 360 CAM data. */
	cam_minutes_per_part: number | null;
	cam_minutes_total: number | null;
	/** Machine model of the CAM export the run time comes from. */
	cam_machine_model: string | null;
	cam_upload_count: number;
	latest_cam_upload_at: string | null;
	optimized: boolean;
	optimized_by_name: string | null;
	optimized_at: string | null;
	reuploaded_since_optimized: boolean;
}
